import React, { Component } from 'react';
import {
    Button,
    Box,
    Grid,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { withTranslation } from 'react-i18next';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { withRouter } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersLayout } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { HelpOutline } from '@mui/icons-material';
import { getTravelDataWC } from '../../../services/AssignmentsService';
import { COLORS } from '../../../utils/Application_Constants';
import CarRentalV3 from './CarRental/CarRentalV3';
import LodgingV3 from './Lodging/LodgingV3';
import FlightsV3 from './Flights/FlightsV3';

const moment = extendMoment(Moment);
const styles = () => ({
    header: {
        fontWeight: '700',
        fontSize: '20px',
        color: COLORS.RAVEN,
        margin: 0,
        textAlign: 'left',
    },
    noDocumentsCard: {
        padding: '24px',
        margin: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
    },
    noDocumentsContent: {
        flex: 1,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
    },
    noDocumentsText: {
        display: 'flex',
        alignItems: 'center',
        margin: 0,
    },
    headerGridRoot: {
        paddingLeft: '25px',
    },
    helpNumber: {
        margin: 0,
        paddingRight: '25px',
        textAlign: 'right',
    },
    dateFilter: {
        margin: 0,
        textAlign: 'center',
    },
    dateWrapper: {
        display: 'flex',
        alignItems: 'center',
    },
    filterButton: {
        fontSize: '16px',
        marginLeft: '8px',
        marginBottom: '3px',
        color: COLORS.TRUE_BLUE,
        '&:hover': {
            background: COLORS.LT_JOURNEY10,
            color: COLORS.LT_JOURNEY,
            borderColor: COLORS.LT_JOURNEY,
        },
    },
    summaryHeaderGridRoot: {
        color: COLORS.RAVEN,
        padding: '25px 15px 0px 15px',
        textAlign: 'left',
    },
    dashboardCard: {
        width: '100%',
        marginTop: '20px',
        paddingTop: '20px',
        paddingBottom: '20px',
    },
    travelCard: {
        border: `1px solid ${COLORS.WHITE_LILAC}`,
        borderRadius: '10px',
        marginTop: '8px',
        marginLeft: '8px',
        marginRight: '8px',
        marginBottom: '8px',
    },
    panelHeader: {
        color: COLORS.RAVEN,
    },
    AccordionSummaryRoot: {
        padding: '8px 20px',
    },
    chevronRoot: {
        width: '25px',
        right: '0',
        color: COLORS.TRUE_BLUE,
    },
    clickableLink: {
        fontWeight: '500',
        color: COLORS.TRUE_BLUE,
        fontSize: '16px',
        cursor: 'pointer',
    },
    alignLeft: {
        width: '50%',
        float: 'left',
        textAlign: 'left',
    },
    iconClass: {
        verticalAlign: 'middle',
        paddingRight: '6px',
        color: COLORS.TRUE_BLUE,
    },
    alert: {
        textAlign: 'center',
        paddingRight: '20px',
    },
    question: {
        fontWeight: 600,
        color: COLORS.RAVEN,
    },
    answer: {
        fontWeight: 400,
        color: COLORS.RAVEN,
        marginBottom: '24px',
    },
});

function CustomActionBar(props) {
    const { onAccept, onClear } = props;

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                px: 2,
                pb: 2,
            }}
        >
            <Button onClick={onClear} color="primary">
                Clear
            </Button>
            <Button variant="contained" color="primary" onClick={onAccept}>
                Apply
            </Button>
        </Box>
    );
}

class TravelV3 extends Component {
    constructor(props) {
        super(props);
        this.dateFilterRef = React.createRef();
        const { assignment } = props;
        const queryString = window.location.hash.split('?')[1];
        const searchParams = new URLSearchParams(queryString);
        const OriginatedDate = searchParams.get('OriginatedDate');
        const CompletedDate = searchParams.get('CompletedDate');
        const todayMoment = moment.utc(new Date()).startOf('day');
        const assignmentStartMoment = moment.utc(assignment.startDate).startOf('day');
        const assignmentEndMoment = moment.utc(assignment.endDate).startOf('day');
        const diffTodayToAssignmentStart = todayMoment.diff(assignmentStartMoment, 'days');
        const diffTodayToAssignmentEnd = todayMoment.diff(assignmentEndMoment, 'days');

        let defaultStartDate = null;
        let defaultEndDate = null;

        // TDS-8942 Default Rules
        if (diffTodayToAssignmentStart < -7) {
            // If the current date is more than 7 days before the assignment start date:
            // Default the start date to one week before the assignment start date.
            defaultStartDate = assignmentStartMoment.clone().add(-7, 'days').format('YYYY-MM-DD');
            // Default the end date to one week after the assignment start date.
            defaultEndDate = assignmentStartMoment.clone().add(6, 'days').format('YYYY-MM-DD');
        } else if (diffTodayToAssignmentStart >= -7 && diffTodayToAssignmentStart < 0) {
            // If the current date is within 7 days of the assignment start date:
            // Default the start date to the current date
            defaultStartDate = todayMoment.format('YYYY-MM-DD');
            // Default the end date to 6 days after the start date
            defaultEndDate = todayMoment.clone().add(6, 'days').format('YYYY-MM-DD');
        } else if (diffTodayToAssignmentEnd === 0) {
            // If the current date is on the assignment end date:
            // Default the start date to the current date
            defaultStartDate = todayMoment.format('YYYY-MM-DD');
            // Default the end date to 6 days after the start date
            defaultEndDate = todayMoment.clone().add(6, 'days').format('YYYY-MM-DD');
        } else if (diffTodayToAssignmentEnd > 0) {
            // If the current date is after the assignment end date:
            // Default the start date to one week before the assignment end date.
            defaultStartDate = assignmentEndMoment.clone().add(-6, 'days').format('YYYY-MM-DD');
            // Default the end date to one week after the assignment end date.
            defaultEndDate = assignmentEndMoment.clone().add(7, 'days').format('YYYY-MM-DD');
        } else {
            // If the assignment is active:
            // Default the start date to the current date
            defaultStartDate = todayMoment.format('YYYY-MM-DD');
            // Default the end date to 6 days after the start date
            defaultEndDate = todayMoment.clone().add(6, 'days').format('YYYY-MM-DD');
        }
        let displayStartDate = defaultStartDate;
        let displayEndDate = defaultEndDate;
        const minDate = assignmentStartMoment.clone().add(-7, 'days').format('YYYY-MM-DD');
        const maxDate = assignmentEndMoment.clone().add(7, 'days').format('YYYY-MM-DD');

        const dateOriginatedDate = moment.utc(OriginatedDate).startOf('day');
        const dateCompletedDate = moment.utc(CompletedDate).startOf('day');

        // Override StartDate display and default with Query Param values when entering with Notification
        if (OriginatedDate) {
            if (dateOriginatedDate.isSameOrAfter(assignmentStartMoment.clone().add(-7, 'days'))) {
                displayStartDate = dateOriginatedDate.format('YYYY-MM-DD');
            } else {
                displayStartDate = minDate;
            }
            defaultStartDate = displayStartDate;
        }
        // Override EndDate display and default with Query Param values when entering with Notification
        if (CompletedDate) {
            if (dateCompletedDate.isSameOrBefore(assignmentEndMoment.clone().add(7, 'days'))) {
                displayEndDate = dateCompletedDate.format('YYYY-MM-DD');
            } else {
                displayEndDate = maxDate;
            }
            defaultEndDate = displayEndDate;
        }
        this.state = {
            travelInfo: [],
            displayTravelInfo: [],
            defaultStartDate,
            defaultEndDate,
            displayStartDate,
            displayEndDate,
            minDate,
            maxDate,
            anchorEl: null,
            isLoading: true,
            originatedDate: OriginatedDate,
            completedDate: CompletedDate,
            scrollIntoView: false,
            disclaimerOpen: false,
            datePickerOpen: false,
        };
    }

    async componentDidMount() {
        const { assignment, externalId } = this.props;
        const assignmentId = assignment.name.slice(4);
        const state = { ...this.state };
        try {
            const aTravelInfo = await getTravelDataWC(externalId, assignmentId, null);
            state.travelInfo = aTravelInfo;
            const displayRange = moment.range(
                moment.utc(state.displayStartDate).startOf('day'),
                moment.utc(state.displayEndDate).endOf('day')
            );
            state.displayTravelInfo = aTravelInfo.filter((segment) =>
                displayRange.overlaps(
                    moment.range(
                        moment.utc(segment.originatedDate).format('YYYY-MM-DD'),
                        moment.utc(segment.completedDate).format('YYYY-MM-DD')
                    )
                )
            );
        } catch (error) {
            const { status } = error.response || { status: undefined };
            if (status === 404) {
                window.location.href = '#/404';
            } else if (status === 503) {
                window.location.href = '#/503';
            } else if (status === 500) {
                window.location.href = '#/500';
            } else {
                // default to 403
                window.location.href = '#/403';
            }
        }
        const queryString = window.location.hash.split('?')[1];
        const aSearchParams = new URLSearchParams(queryString);
        const aOriginatedDate = aSearchParams.get('OriginatedDate');
        const aCompletedDate = aSearchParams.get('CompletedDate');
        if (aOriginatedDate && aCompletedDate) {
            state.scrollIntoView = true;
        }
        state.isLoading = false;
        this.setState({ ...state });
    }

    componentDidUpdate(prevProps) {
        const { assignment } = prevProps;
        const state = { ...this.state };
        // Check to see if Notification was clicked while on the Travel Page
        // if so it needs to update the dates based upon the item that was clicked
        const querString = window.location.hash.split('?')[1];
        const aSearchParams = new URLSearchParams(querString);
        const aOriginatedDate = aSearchParams.get('OriginatedDate');
        const aCompletedDate = aSearchParams.get('CompletedDate');
        if (
            assignment != null &&
            state.travelInfo &&
            (state.originatedDate !== aOriginatedDate || state.completedDate !== aCompletedDate)
        ) {
            const todayMoment = moment(new Date()).startOf('day');
            const assignmentStartMoment = moment.utc(assignment.startDate).startOf('day');
            const assignmentEndMoment = moment.utc(assignment.endDate).startOf('day');
            const diffTodayToAssignmentStart = todayMoment.diff(assignmentStartMoment, 'days');
            const diffTodayToAssignmentEnd = todayMoment.diff(assignmentEndMoment, 'days');

            let defaultStartDate = null;
            let defaultEndDate = null;

            // TDS-8942 Default Rules
            if (diffTodayToAssignmentStart < -7) {
                // If the current date is more than 7 days before the assignment start date:
                // Default the start date to one week before the assignment start date.
                defaultStartDate = assignmentStartMoment
                    .clone()
                    .add(-7, 'days')
                    .format('YYYY-MM-DD');
                // Default the end date to one week after the assignment start date.
                defaultEndDate = assignmentStartMoment.clone().add(7, 'days').format('YYYY-MM-DD');
            } else if (diffTodayToAssignmentStart >= -7 && diffTodayToAssignmentStart < 0) {
                // If the current date is within 7 days of the assignment start date:
                // Default the start date to the current date
                defaultStartDate = todayMoment.format('YYYY-MM-DD');
                // Default the end date to 6 days after the start date
                defaultEndDate = todayMoment.clone().add(6, 'days').format('YYYY-MM-DD');
            } else if (diffTodayToAssignmentEnd === 0) {
                // If the current date is on the assignment end date:
                // Default the start date to the current date
                defaultStartDate = todayMoment.format('YYYY-MM-DD');
                // Default the end date to 6 days after the start date
                defaultEndDate = todayMoment.clone().add(6, 'days').format('YYYY-MM-DD');
            } else if (diffTodayToAssignmentEnd > 0) {
                // If the current date is after the assignment end date:
                // Default the start date to one week before the assignment end date.
                defaultStartDate = assignmentEndMoment.clone().add(-7, 'days').format('YYYY-MM-DD');
                // Default the end date to one week after the assignment end date.
                defaultEndDate = assignmentEndMoment.clone().add(7, 'days').format('YYYY-MM-DD');
            } else {
                // If the assignment is active:
                // Default the start date to the current date
                defaultStartDate = todayMoment.format('YYYY-MM-DD');
                // Default the end date to 6 days after the start date
                defaultEndDate = todayMoment.clone().add(6, 'days').format('YYYY-MM-DD');
            }

            let displayStartDate = defaultStartDate;
            let displayEndDate = defaultEndDate;
            const minDate = assignmentStartMoment.clone().add(-7, 'days').format('YYYY-MM-DD');
            const maxDate = assignmentEndMoment.clone().add(7, 'days').format('YYYY-MM-DD');

            const dateOriginatedDate = moment.utc(aOriginatedDate).startOf('day');
            const dateCompletedDate = moment.utc(aCompletedDate).startOf('day');

            // Override StartDate display and default with Query Param values when entering with Notification
            if (aOriginatedDate) {
                if (
                    dateOriginatedDate.isSameOrAfter(assignmentStartMoment.clone().add(-7, 'days'))
                ) {
                    displayStartDate = dateOriginatedDate.format('YYYY-MM-DD');
                } else {
                    displayStartDate = minDate;
                }
                defaultStartDate = displayStartDate;
            }
            // Override EndDate display and default with Query Param values when entering with Notification
            if (aCompletedDate) {
                if (dateCompletedDate.isSameOrBefore(assignmentEndMoment.clone().add(7, 'days'))) {
                    displayEndDate = dateCompletedDate.format('YYYY-MM-DD');
                } else {
                    displayEndDate = maxDate;
                }
                defaultEndDate = displayEndDate;
            }

            const aDisplayRange = moment.range(
                moment.utc(displayStartDate).startOf('day'),
                moment.utc(displayEndDate).endOf('day')
            );
            const aTravelInfo = state.travelInfo.filter((segment) =>
                aDisplayRange.overlaps(
                    moment.range(
                        moment.utc(segment.originatedDate).startOf('day'),
                        moment.utc(segment.completedDate).endOf('day')
                    )
                )
            );
            this.setState({
                displayTravelInfo: aTravelInfo,
                defaultStartDate,
                defaultEndDate,
                displayStartDate,
                displayEndDate,
                minDate,
                maxDate,
                originatedDate: aOriginatedDate,
                completedDate: aCompletedDate,
                anchorEl: null,
                scrollIntoView: true,
            });
        }
        setTimeout(() => {
            // Adding a slight delay to allow the page to load before scrolling to the element
            this.scrollToMyElement();
        }, 2000); // 2 seconds delay
    }

    clear = () => {
        this.setState((prevState) => ({
            ...prevState,
            displayStartDate: prevState.defaultStartDate,
            displayEndDate: prevState.defaultEndDate,
        }));
    };

    handleOpenDisclaimer = () => {
        window.heap.track('ltoe-travel-disclaimer-tapped', {
            key: 'ltoe-travel-disclaimer-tapped',
            description: 'A user tapped the travel disclaimer',
        });
        this.setState({ disclaimerOpen: true });
    };

    handleCloseDisclaimer = () => {
        this.setState({ disclaimerOpen: false });
    };

    handleOpenFaqs = () => {
        window.heap.track('ltoe-travel-faq-tapped', {
            key: 'ltoe-travel-faq-tapped',
            description: 'A user tapped the travel faqs',
        });
        this.setState({ faqsOpen: true });
    };

    handleCloseFaqs = () => {
        this.setState({ faqsOpen: false });
    };

    handleFilterApply = async () => {
        const state = { ...this.state };
        const { isLoading } = this.state;
        if (!isLoading) {
            this.setState({ isLoading: true });
            try {
                const displayRange = moment.range(
                    moment.utc(state.displayStartDate).startOf('day'),
                    moment.utc(state.displayEndDate).endOf('day')
                );
                const aTravelInfo = state.travelInfo.filter((segment) =>
                    displayRange.overlaps(
                        moment.range(
                            moment.utc(segment.originatedDate).startOf('day'),
                            moment.utc(segment.completedDate).endOf('day')
                        )
                    )
                );
                this.setState({
                    displayTravelInfo: aTravelInfo,
                    anchorEl: null,
                    isLoading: false,
                    datePickerOpen: false,
                });
            } catch (error) {
                const { status } = error.response || { status: undefined };
                if (status === 404) {
                    window.location.href = '#/404';
                } else if (status === 503) {
                    window.location.href = '#/503';
                } else if (status === 500) {
                    window.location.href = '#/500';
                } else {
                    // default to 403
                    window.location.href = '#/403';
                }
            }
        }
    };

    renderDateFilter = () => {
        const { classes } = this.props;
        const {
            displayStartDate,
            displayEndDate,
            minDate,
            maxDate,
            datePickerOpen,
            defaultStartDate,
            defaultEndDate,
        } = this.state;
        const value = [
            displayStartDate ? moment(displayStartDate) : null,
            displayEndDate ? moment(displayEndDate) : null,
        ];

        return (
            <Grid item>
                <div className={classes.dateWrapper}>
                    {displayStartDate && displayEndDate
                        ? `${moment(displayStartDate).format('ddd, MMM D YYYY')} - ${moment(
                              displayEndDate
                          ).format('ddd, MMM D YYYY')}`
                        : 'Select a date range'}

                    <button
                        type="button"
                        onClick={() => this.setState({ datePickerOpen: true })}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                        }}
                    >
                        <CalendarTodayIcon classes={{ root: classes.filterButton }} />
                    </button>
                </div>

                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateRangePicker
                        calendars={2}
                        value={value}
                        open={datePickerOpen}
                        closeOnSelect={false}
                        onClose={() => this.setState({ datePickerOpen: false })}
                        onChange={(newValue) => {
                            if (!newValue || newValue[0] === null || newValue[1] === null) {
                                // Clear clicked or user cleared value
                                this.setState({
                                    displayStartDate: defaultStartDate,
                                    displayEndDate: defaultEndDate,
                                });
                            } else {
                                const [start, end] = newValue;
                                this.setState({
                                    displayStartDate: start ? start.toDate() : null,
                                    displayEndDate: end ? end.toDate() : null,
                                });
                            }
                        }}
                        minDate={minDate ? moment(minDate) : undefined}
                        maxDate={maxDate ? moment(maxDate) : undefined}
                        dayOfWeekFormatter={(day) => day.format('ddd').toUpperCase()}
                        slots={{
                            layout: PickersLayout,
                            actionBar: CustomActionBar,
                        }}
                        slotProps={{
                            layout: {
                                sx: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                },
                            },
                            calendarHeader: {
                                sx: {
                                    justifyContent: 'center',
                                },
                            },
                            actionBar: {
                                actionBar: { actions: [] },
                                onAccept: () => this.handleFilterApply(),
                                onClear: () => this.clear(),
                            },
                            textField: { sx: { display: 'none' } },
                            popper: {
                                placement: 'bottom',
                                strategy: 'absolute',
                                anchorEl: () => this.dateFilterRef.current,
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: ({ popper, reference }) => {
                                                const referenceCenterX =
                                                    reference.left + reference.width / 2;
                                                const popperCenterX = popper.width / 2;
                                                const offsetX =
                                                    referenceCenterX -
                                                    popperCenterX -
                                                    reference.left;

                                                return [offsetX, 8];
                                            },
                                        },
                                    },
                                ],
                            },
                        }}
                    />
                </LocalizationProvider>
            </Grid>
        );
    };

    renderDisclaimerModal = () => {
        const { classes, t } = this.props;
        const { disclaimerOpen } = this.state;
        return (
            <Dialog open={disclaimerOpen} onClose={this.handleCloseDisclaimer}>
                <DialogTitle sx={{ m: 0, p: 2 }}>
                    Travel Itinerary Disclaimer
                    <IconButton
                        aria-label="close"
                        onClick={this.handleCloseDisclaimer}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom sx={{ fontWeight: 400 }}>
                        The travel details shown are based on the most recent information available.
                        For real-time updates, please check with your airline provider. For general
                        questions contact your Clinician Relations Specialist or if this is an
                        after-hours emergency contact&nbsp;
                        <a
                            href={t('common:INFORMATION.HELP_NUMBER_LINK')}
                            className={classes.clickableLink}
                        >
                            {t('common:INFORMATION.HELP_NUMBER')}
                        </a>
                        .
                    </Typography>
                </DialogContent>
            </Dialog>
        );
    };

    renderFaqsModal = () => {
        const { classes, t } = this.props;
        const { faqsOpen } = this.state;

        return (
            <Dialog open={faqsOpen} onClose={this.handleCloseFaqs} maxWidth="md" fullWidth>
                <DialogTitle>
                    Travel
                    <IconButton
                        aria-label="close"
                        onClick={this.handleCloseFaqs}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom className={classes.question}>
                        What should I do if I experience a travel emergency?
                    </Typography>
                    <Typography variant="body1" className={classes.answer}>
                        Unfortunately, travel emergencies happen. It&apos;s inevitable. But we want
                        you to know we&apos;re here for you, any day, any time. If you experience
                        issues outside of regular business hours and your question isn&apos;t
                        answered below, contact our after-hours service for immediate
                        assistance:&nbsp;
                        <a
                            href={t('common:INFORMATION.HELP_NUMBER_LINK')}
                            className={classes.clickableLink}
                        >
                            {t('common:INFORMATION.HELP_NUMBER')}
                        </a>
                        .
                    </Typography>

                    <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ mt: 4, fontWeight: 600, color: COLORS.RAVEN }}
                    >
                        Flights
                    </Typography>
                    <Typography variant="body1" gutterBottom className={classes.question}>
                        What should I do if my flight gets canceled?
                    </Typography>
                    <Typography variant="body1" className={classes.answer}>
                        If you are at the airport and your flight gets canceled, check directly with
                        the airline first. Most of the time, they&apos;ll already have you confirmed
                        on the next available flight.
                    </Typography>
                    <Typography variant="body1" className={classes.answer}>
                        If a canceled flight is going to make you late to your assignment, please
                        notify your recruiter or call our after-hours number. If you&apos;re not at
                        the airport and find out your flight has been canceled, contact your
                        recruiter or call the after-hours number so we can arrange a new flight.
                    </Typography>

                    <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ mt: 4, fontWeight: 600, color: COLORS.RAVEN }}
                    >
                        Hotel
                    </Typography>
                    <Typography variant="body1" gutterBottom className={classes.question}>
                        What if the hotel I&apos;m staying at can&apos;t find LocumTenens.com&apos;s
                        credit card authorization?
                    </Typography>
                    <Typography variant="body1" className={classes.answer}>
                        Please ask them to check the fax machine! The credit card authorization
                        forms are always faxed the day before or the morning of your arrival. If
                        they still can&apos;t locate it, click on the e-itinerary link provided by
                        your Clinician Relations Specialist in your travel email, refresh the link
                        and click the fax machine icon to resend the authorization. If the icon
                        doesn&apos;t appear or it still doesn&apos;t go through, contact your
                        Clinician Relations Specialist or our after-hours service. Hotels will
                        typically let you check in even without the authorization.
                    </Typography>

                    <Typography variant="body1" gutterBottom className={classes.question}>
                        The hotel charged my credit card. What should I do?
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 400, color: COLORS.RAVEN }}>
                        The hotel will request your credit card for incidentals, but they should not
                        charge your card for the room or tax. However, if the hotel mistakenly
                        charges your credit card, please notify your Clinician Relations Specialist
                        the next business day, and they will have the charges reversed and put on
                        LocumTenens.com&apos;s credit card. We make every effort to ensure your
                        credit card is not charged for the room and tax.
                    </Typography>

                    <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ mt: 4, fontWeight: 600, color: COLORS.RAVEN }}
                    >
                        Rental Car
                    </Typography>
                    <Typography variant="body1" gutterBottom className={classes.question}>
                        Can I upgrade or add additional equipment to my rental car?
                    </Typography>
                    <Typography variant="body1" className={classes.answer}>
                        Our preferred vendors are Enterprise, Avis, and National. We have direct
                        bill accounts set up with each of these vendors that cover the rental car
                        daily charges and insurance only. You will need to provide a credit card at
                        the counter for upgrades or additional equipment.
                    </Typography>

                    <Typography variant="body1" gutterBottom className={classes.question}>
                        Does LocumTenens.com cover liability and collision insurance?
                    </Typography>
                    <Typography variant="body1" className={classes.answer}>
                        Our corporate contract with Avis, Enterprise, and National includes LDW and
                        additional liability insurance (ALI). LDW will cover any loss or damage to
                        the rented vehicle as long as the damage was not caused due to illegal
                        activity. ALI meets each state&apos;s requirements.
                    </Typography>

                    <Typography variant="body1" gutterBottom className={classes.question}>
                        I am having an issue with my rental car. What should I do?
                    </Typography>
                    <Typography variant="body1" className={classes.answer}>
                        Your rental car should be inspected for damages prior to driving off the
                        lot. If you notice any issues with the car after picking it up, immediately
                        return the car to the counter and exchange it for a new one to avoid
                        unnecessary charges.
                    </Typography>

                    <Typography variant="body1" gutterBottom className={classes.question}>
                        I was involved in a car accident in my rental car. What should I do?
                    </Typography>
                    <Typography variant="body1" className={classes.answer}>
                        If you are involved in an accident and cannot drive the car, you will need
                        to call the roadside assistance number for the rental carrier. Please notify
                        your Clinician Relations Specialist of the incident as soon as possible.
                    </Typography>

                    <Typography variant="body1" gutterBottom className={classes.question}>
                        Roadside assistance numbers:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 400, color: COLORS.RAVEN }}>
                        Avis -{' '}
                        <a href="tel:8003542847" className={classes.clickableLink}>
                            800-354-2847
                        </a>
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 400, color: COLORS.RAVEN }}>
                        Enterprise -{' '}
                        <a href="tel:8003076666" className={classes.clickableLink}>
                            800-307-6666
                        </a>
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 400, color: COLORS.RAVEN }}>
                        National -{' '}
                        <a href="tel:8002277368" className={classes.clickableLink}>
                            800-227-7368
                        </a>
                    </Typography>
                </DialogContent>
            </Dialog>
        );
    };

    scrollToMyElement = () => {
        const state = { ...this.state };
        if (state.scrollIntoView) {
            this.myRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
            state.scrollIntoView = false;
            this.setState({ ...state });
        }
    };

    renderNoDocuments() {
        const { classes } = this.props;
        return (
            <Grid item xs={12}>
                <Paper className={classes.noDocumentsCard}>
                    <div className={classes.noDocumentsContent}>
                        <InfoOutlinedIcon
                            fontSize="large"
                            style={{ color: COLORS.RAVEN, backgroundColor: 'white' }}
                        />
                        <p className={classes.noDocumentsText}>
                            No travel is scheduled for the selected dates. Contact your Clinician
                            Relations Specialist for immediate assistance.
                        </p>
                    </div>
                </Paper>
            </Grid>
        );
    }

    render() {
        const state = { ...this.state };
        const { t, classes, externalId, assignment } = this.props;
        const assignmentId = assignment.name.slice(4);

        let cards;
        if (state.displayTravelInfo.length === 0) {
            cards = this.renderNoDocuments();
        } else {
            cards = state.displayTravelInfo.map((item, index) => {
                switch (item.segmentType) {
                    case 'H':
                        return (
                            <Grid item xs={12} classes={{ root: classes.travelCard }}>
                                <LodgingV3
                                    key={index}
                                    item={item}
                                    externalId={externalId}
                                    assignmentId={assignmentId}
                                />
                            </Grid>
                        );
                    case 'C':
                        return (
                            <Grid item xs={12} classes={{ root: classes.travelCard }}>
                                <CarRentalV3
                                    key={index}
                                    item={item}
                                    externalId={externalId}
                                    assignmentId={assignmentId}
                                />
                            </Grid>
                        );
                    case 'A':
                        return (
                            <Grid item xs={12} classes={{ root: classes.travelCard }}>
                                <FlightsV3
                                    key={index}
                                    item={item}
                                    externalId={externalId}
                                    assignmentId={assignmentId}
                                />
                            </Grid>
                        );
                    default:
                        return null;
                }
            });
        }

        return (
            <Paper classes={{ root: classes.dashboardCard }}>
                <Grid container>
                    <Grid item xs={12} classes={{ root: classes.headerGridRoot }}>
                        <Grid container>
                            <Grid item xs={12} md={4} classes={{ root: classes.header }}>
                                {t('assignments:TRAVEL.TITLEV3')}
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                md={4}
                                classes={{ root: classes.dateFilter }}
                                ref={this.dateFilterRef}
                            >
                                {this.renderDateFilter()}
                            </Grid>
                            <Grid item xs={12} md={4} classes={{ root: classes.helpNumber }}>
                                {t('assignments:TRAVEL.AFTER_HOURS')}{' '}
                                <a
                                    href={t('common:INFORMATION.HELP_NUMBER_LINK')}
                                    className={classes.clickableLink}
                                >
                                    {t('common:INFORMATION.HELP_NUMBER')}
                                </a>
                                <IconButton
                                    aria-label="travel disclaimer"
                                    size="small"
                                    onClick={this.handleOpenDisclaimer}
                                    sx={{ marginLeft: 1, color: COLORS.TRUE_BLUE }}
                                >
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    aria-label="travel faqs"
                                    size="small"
                                    onClick={this.handleOpenFaqs}
                                    sx={{ color: COLORS.TRUE_BLUE }}
                                >
                                    <HelpOutline fontSize="small" />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                    {cards}
                </Grid>
                {this.renderDisclaimerModal()}
                {this.renderFaqsModal()}
            </Paper>
        );
    }
}
export default withTranslation()(withRouter(withStyles(styles)(TravelV3)));
